import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useAuthSync } from '../../../hooks/useAuthSync';
import Button from '../../common/Button';
import TreatmentPlansTable from './TreatmentPlansTable';
import PatientSectionHeader from '../common/PatientSectionHeader';
import TreatmentPlanService from '../../../services/treatmentPlanService';
import OrganizationService from '../../../services/organizationService';
import PatientService from '../../../services/patientService';
import ProfileService from '../../../services/profileService';
import ProcedureService from '../../../services/procedureService';
import AppUtilsService from '../../../services/appUtilsService';
import Modal from '../../common/Modal';
import ToastContainer from '../../common/ToastContainer';
import { useToast } from '../../../hooks/useToast';
import { generateTreatmentPlanPdfBlob, printTreatmentPlan } from '../../../utils/treatmentPlanPrintUtils';
import './TreatmentPlansList.css';

const TreatmentPlansList = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const { hasPermission } = useAuthSync();
  const canViewContactDetails = hasPermission('patient:viewcontactdetails');

  const [treatmentPlans, setTreatmentPlans] = useState([]);
  const [patient, setPatient] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [doctor, setDoctor] = useState(null);
  const [procedureCatalog, setProcedureCatalog] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [emailModalPlan, setEmailModalPlan] = useState(null);
  const [emailDraft, setEmailDraft] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const { toasts, removeToast, success: showSuccess, error: showError } = useToast();

  const fetchAllTreatmentPlans = useCallback(async (token) => {
    const limit = 50;
    let page = 1;
    let totalPages = 1;
    let allPlans = [];

    do {
      const response = await TreatmentPlanService.getTreatmentPlans(token, {
        patientId,
        includeArchived: true,
        page,
        limit,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });

      const payload = response?.data || response;
      const result = payload?.data?.treatmentPlans ? payload.data : payload;
      const plansPage = result?.treatmentPlans || [];
      totalPages = result?.totalPages || 1;

      allPlans = allPlans.concat(plansPage);
      page += 1;
    } while (page <= totalPages);

    return allPlans;
  }, [patientId]);

  // Cargar datos del paciente y planes de tratamiento
  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = auth.user?.access_token;

      if (!token || !patientId) return;

      const userId = auth.user?.profile?.sub || auth.user?.sub;
      const [patientData, plansData, organizationData, doctorData, proceduresData] = await Promise.all([
        PatientService.getPatientById(patientId, token),
        fetchAllTreatmentPlans(token),
        userId ? OrganizationService.getCurrentUserOrganization(userId, token) : Promise.resolve(null),
        userId ? ProfileService.getUserProfile(userId, token).catch(err => {
          console.warn('Could not fetch user profile:', err);
          return null;
        }) : Promise.resolve(null),
        ProcedureService.getAllProcedures(token).catch(err => {
          console.warn('Could not fetch procedures:', err);
          return { data: [] };
        })
      ]);

      setPatient(patientData);
      setOrganization(organizationData);
      setDoctor(doctorData);
      setProcedureCatalog(proceduresData?.data || proceduresData || []);
      const plans = Array.isArray(plansData) ? plansData : (plansData?.data || []);
      setTreatmentPlans(plans);
      setFilteredPlans(plans.filter(plan => !plan.isArchived));
    } catch (error) {
      console.error('Error loading data:', error);
      setMessage({
        type: 'error',
        text: 'Error al cargar los planes de tratamiento'
      });
    } finally {
      setIsLoading(false);
    }
  }, [patientId, auth.user?.access_token, auth.user?.sub, auth.user?.profile?.sub, fetchAllTreatmentPlans]);

  // Cargar datos al montar el componente
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filtrar planes por búsqueda
  useEffect(() => {
    const basePlans = treatmentPlans.filter(plan =>
      activeTab === 'archived' ? plan.isArchived : !plan.isArchived
    );

    if (!searchQuery.trim()) {
      setFilteredPlans(basePlans);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = basePlans.filter(plan =>
      plan.name.toLowerCase().includes(query) ||
      (plan.doctorName && plan.doctorName.toLowerCase().includes(query)) ||
      plan.notes.toLowerCase().includes(query)
    );
    setFilteredPlans(filtered);
  }, [searchQuery, treatmentPlans, activeTab]);



  // Manejar búsqueda
  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  // Ver plan de tratamiento
  const handleViewPlan = (plan) => {
    navigate(`/patients/${patientId}/treatment-plans/${plan.id}`);
  };

  // Editar plan de tratamiento
  const handleEditPlan = (plan) => {
    navigate(`/patients/${patientId}/treatment-plans/${plan.id}/edit`);
  };

  // Eliminar plan de tratamiento
  const handleDeletePlan = async (plan) => {
    if (!window.confirm(`¿Estás seguro de que quieres archivar el plan "${plan.name}"?`)) {
      return;
    }

    try {
      const token = auth.user?.access_token;
      await TreatmentPlanService.deleteTreatmentPlan(plan.id, token);

      setMessage({
        type: 'success',
        text: 'Plan de tratamiento archivado exitosamente'
      });

      // Recargar datos
      loadData();
    } catch (error) {
      console.error('Error deleting treatment plan:', error);
      setMessage({
        type: 'error',
        text: 'Error al eliminar el plan de tratamiento'
      });
    }
  };

  // Cambiar estado del plan
  const handleStatusChange = async (planId, newStatus) => {
    try {
      const token = auth.user?.access_token;
      await TreatmentPlanService.updateTreatmentPlanStatus(planId, newStatus, token);

      setMessage({
        type: 'success',
        text: 'Estado del plan actualizado exitosamente'
      });

      // Actualizar el estado local
      setTreatmentPlans(prev =>
        prev.map(plan =>
          plan.id === planId ? { ...plan, status: newStatus } : plan
        )
      );
    } catch (error) {
      console.error('Error updating plan status:', error);
      setMessage({
        type: 'error',
        text: 'Error al actualizar el estado del plan'
      });
    }
  };

  const handleRestorePlan = async (plan) => {
    try {
      const token = auth.user?.access_token;
      await TreatmentPlanService.updateTreatmentPlan(plan.id, { isArchived: false }, token);

      setMessage({
        type: 'success',
        text: 'Plan de tratamiento restaurado exitosamente'
      });

      loadData();
    } catch (error) {
      console.error('Error restoring treatment plan:', error);
      setMessage({
        type: 'error',
        text: 'Error al restaurar el plan de tratamiento'
      });
    }
  };

  // Crear nuevo plan de tratamiento
  const handleCreateNewPlan = () => {
    navigate(`/patients/${patientId}/treatment-plan`);
  };

  // Imprimir plan de tratamiento
  const handlePrintPlan = (plan) => {
    try {
      printTreatmentPlan(plan, patient, organization?.logoViewUrl, organization, doctor, procedureCatalog);
    } catch (error) {
      console.error('Error printing treatment plan:', error);
      setMessage({
        type: 'error',
        text: 'Error al imprimir el plan de tratamiento: ' + error.message
      });
    }
  };

  const handleGenerateInvoice = (plan) => {
    navigate(`/patients/${patientId}/treatment-plans/${plan.id}/invoice`);
  };

  const handleEmailPlan = (plan) => {
    setEmailModalPlan(plan);
    setEmailDraft(patient?.email || '');
  };

  const handleCloseEmailModal = () => {
    setEmailModalPlan(null);
  };

  const handleSendEmail = async () => {
    const plan = emailModalPlan;
    if (!plan) {
      return;
    }

    const trimmedEmail = emailDraft.trim();

    if (!trimmedEmail) {
      showError('Ingresa un correo electronico para enviar el plan.');
      return;
    }

    if (!AppUtilsService.isValidEmail(trimmedEmail)) {
      showError('Ingresa un correo electronico valido.');
      return;
    }

    try {
      const token = auth.user?.access_token;
      if (!token) {
        showError('No se pudo validar la sesion.');
        return;
      }

      setIsSendingEmail(true);
      const pdfBlob = await generateTreatmentPlanPdfBlob(
        plan,
        patient,
        organization?.logoViewUrl,
        organization,
        doctor,
        procedureCatalog
      );

      const pdfBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = typeof reader.result === 'string' ? reader.result : '';
          const base64 = result.split(',')[1];
          if (!base64) {
            reject(new Error('No se pudo generar el PDF.'));
            return;
          }
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(pdfBlob);
      });

      const fileName = `plan_tratamiento_${(patient?.firstName || 'paciente')}_${(patient?.lastName || '')}_${plan.id}.pdf`
        .replace(/\s+/g, '_')
        .toLowerCase();

      await TreatmentPlanService.sendTreatmentPlanEmail(plan.id, {
        email: trimmedEmail,
        pdfBase64,
        fileName
      }, token);

      showSuccess('Correo enviado correctamente.');
      setEmailModalPlan(null);
    } catch (error) {
      console.error('Error sending treatment plan email:', error);
      showError('No se pudo enviar el correo.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  if (isLoading) {
    return (
      <div className="treatment-plans-list">
        <PatientSectionHeader
          patientId={patientId}
          patientName={patient ? `${patient.firstName} ${patient.lastName}` : undefined}
          sectionTitle="Planes de Tratamiento"
        />
        <div className="treatment-plans-loading">
          <div className="loading-spinner"></div>
          <p>Cargando planes de tratamiento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="treatment-plans-list">
      {/* Header del paciente */}
      <PatientSectionHeader
        patientId={patientId}
        patientName={patient ? `${patient.firstName} ${patient.lastName}` : undefined}
        sectionTitle="Planes de Tratamiento"
        actionButton={
          <Button
            variant="primary"
            onClick={handleCreateNewPlan}
            icon="fas fa-plus"
          >
            Nuevo Plan de Tratamiento
          </Button>
        }
      />

      {/* Mensajes */}
      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
          <button
            className="message-close"
            onClick={() => setMessage({ type: '', text: '' })}
          >
            ×
          </button>
        </div>
      )}



      {/* Tabs */}
      <div className="treatment-plans-tabs">
        <button
          className={`tab ${activeTab === 'active' ? 'active' : ''}`}
          onClick={() => setActiveTab('active')}
        >
          <i className="fas fa-clipboard-list"></i>
          Planes Activos
        </button>
        <button
          className={`tab ${activeTab === 'archived' ? 'active' : ''}`}
          onClick={() => setActiveTab('archived')}
        >
          <i className="fas fa-archive"></i>
          Planes Archivados
        </button>
      </div>

      {/* Controles superiores */}
      <div className="treatment-plans-controls">
        <div className="search-section">
          <div className="search-input-container">
            <i className="fas fa-search search-icon"></i>
            <input
              type="text"
              placeholder="Buscar planes de tratamiento..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button
                className="clear-search"
                onClick={() => handleSearch('')}
                title="Limpiar búsqueda"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Lista de planes de tratamiento */}
      <div className="treatment-plans-content">
        {filteredPlans.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-clipboard-list"></i>
            <h3>No hay planes de tratamiento</h3>
            <p>
              {searchQuery
                ? `No se encontraron planes que coincidan con "${searchQuery}"`
                : activeTab === 'archived'
                  ? 'Este paciente no tiene planes archivados'
                  : 'Este paciente aún no tiene planes de tratamiento creados'
              }
            </p>
            {searchQuery ? (
              <Button
                variant="outline"
                onClick={() => handleSearch('')}
              >
                Ver todos los planes
              </Button>
            ) : activeTab === 'active' ? (
              <Button
                variant="primary"
                onClick={handleCreateNewPlan}
              >
                Crear primer plan
              </Button>
            ) : null}
          </div>
        ) : (
          <TreatmentPlansTable
            treatmentPlans={filteredPlans}
            onView={handleViewPlan}
            onEdit={handleEditPlan}
            onDelete={handleDeletePlan}
            onPrint={handlePrintPlan}
            onEmail={canViewContactDetails ? handleEmailPlan : undefined}
            onGenerateInvoice={handleGenerateInvoice}
            onStatusChange={handleStatusChange}
            onRestore={handleRestorePlan}
            readOnly={activeTab === 'archived'}
            searchQuery={searchQuery}
          />
        )}
      </div>
      <Modal
        isOpen={Boolean(emailModalPlan)}
        title="Enviar plan de tratamiento"
        onClose={handleCloseEmailModal}
        footer={
          <>
            <button className="btn btn-secondary" onClick={handleCloseEmailModal}>
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSendEmail}
              disabled={isSendingEmail}
            >
              {isSendingEmail ? <span className="email-send-spinner" aria-hidden="true"></span> : 'Enviar'}
            </button>
          </>
        }
      >
        <div className="email-modal-content">
          <p className="email-modal-description">
            Se enviara un PDF con el plan de tratamiento al correo seleccionado.
          </p>
          <label className="email-modal-label" htmlFor="treatment-plan-email">
            Correo electronico
          </label>
          <input
            id="treatment-plan-email"
            type="email"
            className="email-modal-input"
            value={emailDraft}
            onChange={(event) => setEmailDraft(event.target.value)}
            placeholder="correo@ejemplo.com"
          />
        </div>
      </Modal>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default TreatmentPlansList;
